import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Constants
export const DAY_NAMES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June', 
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Interfaces 
interface TimeSlot {
  id: string;
  startTime: Date;
  endTime: Date;
  formattedTime: string;
}

interface DayInfo {
  date: Date;
  dayName: string;
  dayNumber: number;
  month: string;
  isToday: boolean;
  isSelected: boolean;
  hasSlots: boolean;
}

// Helper Functions
export const createDefaultForm = (fb: FormBuilder): FormGroup => {
  return fb.group({
    name: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]]
  });
};

export const setupFormFields = (fb: FormBuilder, fields: any[] = []) => {
  const formConfig: Record<string, any> = {
    name: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]]
  };
  
  const customFields = fields || [];
  
  // Add custom fields to form
  customFields.forEach(field => {
    const validators = field.required ? [Validators.required] : [];
    formConfig[field.id] = ['', validators];
  });
  
  return {
    form: fb.group(formConfig),
    customFields
  };
};

export const formatTimeHelper = (date: Date): string => {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  
  hours = hours % 12 || 12; // Convert 0 to 12
  const minutesStr = minutes < 10 ? '0' + minutes : minutes;
  
  return `${hours}:${minutesStr} ${ampm}`;
};

export const processTimeSlots = (times: any[] = []): Record<string, TimeSlot[]> => {
  if (!times?.length) return {};
  
  // Group the times by date
  return times.reduce((acc: Record<string, TimeSlot[]>, time: any) => {
    const startTime = new Date(time.startTime);
    const dateStr = startTime.toDateString();
    
    if (!acc[dateStr]) {
      acc[dateStr] = [];
    }
    
    acc[dateStr].push({
      id: time.id,
      startTime,
      endTime: new Date(time.endTime),
      formattedTime: formatTimeHelper(startTime)
    });
    
    return acc;
  }, {});
};

export const isSameDay = (date1: Date, date2: Date): boolean => {
  return date1.getDate() === date2.getDate() && 
         date1.getMonth() === date2.getMonth() && 
         date1.getFullYear() === date2.getFullYear();
};

export const generateWeekDaysArray = (
  currentDate: Date, 
  today: Date, 
  timesByDate: Record<string, TimeSlot[]>
): DayInfo[] => {
  const currentDay = currentDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
  
  // Find the date of the previous Sunday to start our week
  const startDate = new Date(currentDate);
  startDate.setDate(currentDate.getDate() - currentDay);
  
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    
    const dateStr = date.toDateString();
    const hasSlots = Boolean(timesByDate[dateStr]?.length);
    
    return {
      date,
      dayName: DAY_NAMES[date.getDay()],
      dayNumber: date.getDate(),
      month: MONTH_NAMES[date.getMonth()],
      isToday: isSameDay(date, today),
      isSelected: false,
      hasSlots
    };
  });
};

export const formatDurationText = (startTime: string | Date, endTime: string | Date): string => {
  const start = new Date(startTime);
  const end = new Date(endTime);
  
  // Calculate duration in minutes
  const durationMs = end.getTime() - start.getTime();
  const durationMinutes = Math.round(durationMs / (1000 * 60));
  
  if (durationMinutes >= 60) {
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    return `${hours} Hour${hours > 1 ? 's' : ''}${minutes > 0 ? ` ${minutes} Minute${minutes > 1 ? 's' : ''}` : ''} Meeting`;
  }
  
  return `${durationMinutes} Minute Meeting`;
};